//----------------------------------------------------------
// Interactive 3-set Inclusion–Exclusion demo
//   • circle radii scale with |A|, |B|, |C|
//   • region labels update live
//----------------------------------------------------------
import { useState, useEffect, useRef } from 'react';
import * as d3 from 'd3';

export default function InclusionExclusionTool({
  width  = 440,
  height = 360,
  style  = {}
}) {
  /* ---------- 1. region state ---------- */
  const [v, setV] = useState({
    a: 16, b: 15, c: 14,
    ab: 3, ac: 8, bc: 4, abc: 1
  });

  /* ---------- 2. derived totals & union ---------- */
  const totA = v.a + v.ab + v.ac + v.abc;
  const totB = v.b + v.ab + v.bc + v.abc;
  const totC = v.c + v.ac + v.bc + v.abc;
  const union = totA + totB + totC - (v.ab + v.ac + v.bc + v.abc);

  /* ---------- 3. dynamic radii (sqrt-area scaling) ---------- */
  // pick a max visual radius then scale others
  const MAX_R = 70;
  const maxTot = Math.max(totA, totB, totC, 1);     // avoid /0
  const rA = MAX_R * Math.sqrt(totA / maxTot);
  const rB = MAX_R * Math.sqrt(totB / maxTot);
  const rC = MAX_R * Math.sqrt(totC / maxTot);

  /* ---------- 4. SVG drawing ---------- */
  const svgRef = useRef(null);
  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // fixed centres (overlap kept simple)
    const cx = width * 0.50, cy = height * 0.45;
    const pos = {
      A: { cx: cx - MAX_R * 0.8, cy, r: rA, col: '#4D9DE0' },
      B: { cx: cx + MAX_R * 0.8, cy, r: rB, col: '#E15554' },
      C: { cx,                   cy: cy + MAX_R * 0.9, r: rC, col: '#3BB273' }
    };

    // backdrop
    svg.append('rect')
       .attr('width', width).attr('height', height)
       .attr('fill', '#fff');

    // circles
    Object.values(pos).forEach(p =>
      svg.append('circle')
         .attr('cx', p.cx).attr('cy', p.cy).attr('r', p.r)
         .attr('fill', p.col).attr('fill-opacity', 0.18)
         .attr('stroke', p.col).attr('stroke-width', 2)
    );

    // helper for text
    const label = (x, y, txt) => svg.append('text')
      .attr('x', x).attr('y', y)
      .attr('text-anchor', 'middle')
      .style('font-size', 12)
      .text(txt);

    // single-set labels (slightly toward centre)
    label(pos.A.cx, pos.A.cy - pos.A.r / 2.2, totA);
    label(pos.B.cx, pos.B.cy - pos.B.r / 2.2, totB);
    label(pos.C.cx, pos.C.cy + pos.C.r / 2.2, totC);

    // pairwise intersection approximate mid-points
    label((pos.A.cx + pos.B.cx) / 2, cy - 18, v.ab);
    label((pos.A.cx + pos.C.cx) / 2 - 5, (pos.A.cy + pos.C.cy) / 2 + 16, v.ac);
    label((pos.B.cx + pos.C.cx) / 2 + 5, (pos.B.cy + pos.C.cy) / 2 + 16, v.bc);

    // triple intersection
    label(cx, cy + 4, v.abc);

    // set names under circles
    Object.entries(pos).forEach(([id, p]) =>
        svg.append("text")
           .attr("x", p.cx)
           .attr("y", p.cy + 4)        // a hair below true centre
           .attr("text-anchor", "middle")
           .style("font-weight", 700)
           .style("font-size", 16)
           .text(id)
      );
  }, [rA, rB, rC, v, width, height, totA, totB, totC]);

  /* ---------- 5. number field factory ---------- */
  const num = key => (
    <label style={{ marginRight: 10 }}>
      |{key}|{' '}
      <input
      className="inputBox"
        type="number"
        value={v[key]}
        min={0}
        onChange={e => setV({ ...v, [key]: +e.target.value })}
        style={{ width: 50 }}
      />
    </label>
  );

  /* ---------- 6. render ---------- */
  return (
    <div style={{ display: 'flex', ...style }}>
      <svg
        ref={svgRef}
        width={width}
        height={height}
        style={{ border: '1px solid #ccc', marginRight: 16 }}
      />

      <div style={{ flex: 1, fontFamily: 'sans-serif' }}>
        <div style={{ marginBottom: 6 }}>
          {num('a')} {num('b')} {num('c')}
        </div>
        <div style={{ marginBottom: 6 }}>
          {num('ab')} {num('ac')} {num('bc')}
        </div>
        <div style={{ marginBottom: 6 }}>
          {num('abc')}
        </div>

        <hr />
        <p style={{ fontSize: 24, margin: 0 }}>
          <strong>Union size: {union}</strong>
        </p>
        <p style={{ fontSize: 24, fontStyle: 'italic' }}>
          Calculated with the Inclusion–Exclusion Principle.
        </p>
      </div>
    </div>
  );
}
